import type { FormMode } from 'Consts/formModes';
import { FORM_MODES } from 'Consts/formModes';

import type {
  IDatacenterModel as DatacenterModel,
  IOrganizationModel as FormOrganizationModel
} from '@libs/models';

import type { CourseFormData, CourseFormOptions } from './types';

import type { TimezoneModel } from 'Api';

export const mapOrganizationOptions = (organizations: FormOrganizationModel[]) =>
  organizations.map(organization => ({
    id: organization.id,
    label: organization.name,
    description: ''
  }));

export const mapTimezoneOptions = (timezones: TimezoneModel[]) =>
  timezones.map((timezone: TimezoneModel) => ({
    id: timezone.id,
    label: timezone.label
  }));

export const mapDatacenterOptions = (datacenters: DatacenterModel[]) =>
  datacenters.map(datacenter => ({
    id: datacenter.id.toString(),
    label: datacenter.name,
    description: ''
  }));

export const getDefaultFormValues = (
  formOptions: CourseFormOptions | null,
  mode: FormMode,
  initialData?: Partial<CourseFormData>,
  userTimezone?: string
) => {
  if (!formOptions) {
    throw new Error('Form options are required to generate default values');
  }

  const defaultDataCenterId = formOptions.datacenters[0]?.id?.toString();
  const defaultOrganizationId = formOptions.organizations[0]?.id;
  const defaultTimezone = userTimezone ?? 'UTC';

  if (!defaultOrganizationId && formOptions.organizations.length === 0) {
    throw new Error('At least one organization must be available to create or edit courses');
  }

  const isEditMode = mode === FORM_MODES.EDIT;

  if (isEditMode && initialData) {
    const effectiveTimezone = initialData.timezone ?? defaultTimezone;
    const dataCenterIds = Array.isArray(initialData.daDataCenterIds)
      ? initialData.daDataCenterIds
      : [];
    const organizationId = initialData.daOrganizationId ?? defaultOrganizationId;

    return {
      title: initialData.title ?? '',
      description: initialData.description ?? '',
      timezone: effectiveTimezone,
      maxParticipants: initialData.maxParticipants ?? 20,
      expectedParticipants: initialData.expectedParticipants ?? 10,
      daDataCenterIds: dataCenterIds,
      daOrganizationId: organizationId,
      wantToBeCoordinator: initialData.wantToBeCoordinator ?? false,
      isDemo: initialData.isDemo ?? false,
      wantToBeTrainer: initialData.wantToBeTrainer ?? false,
      courseDates: initialData.courseDates ?? []
    };
  }

  return {
    title: '',
    description: '',
    timezone: defaultTimezone,
    maxParticipants: 20,
    expectedParticipants: 10,
    daDataCenterIds: defaultDataCenterId ? [defaultDataCenterId] : [],
    daOrganizationId: defaultOrganizationId,
    wantToBeCoordinator: true,
    isDemo: false,
    wantToBeTrainer: false,
    courseDates: []
  };
};
